import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState
} from 'react';
import {
  ConfigProvider as SharedConfigProvider,
  useConfig as useSharedConfig
} from '@sanlam-glacier/app-common/providers';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { stateService } from 'stores/state-service';
import { validateEnv } from 'utils/env/validate-env';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Env } from '@sanlam-glacier/app-common/types/env/Env.type.ts';
import { environmentService } from '@sanlam-glacier/app-common/stores';

const EnvContext = createContext<(Env & { setConfig: (env: Env) => void }) | undefined>(undefined);
EnvContext.displayName = 'Environment Variables Configuration';

const setTitle = (title: string) => {
  document.title = title;
};

const getEnvUrl = (baseUrl: string, routerBasePath?: string) => {
  const resolvedRouterPath = routerBasePath ?? '';
  const newUrl = baseUrl + resolvedRouterPath + 'config/__ENV.json';
  const formattedUrl = newUrl.replaceAll('//', '/');
  return formattedUrl;
};

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const [doneLoadingConfig, setDoneLoadingConfig] = useState(false);
  const [config, setConfig] = useState({
    apiUrl: import.meta.env.VITE_API_URL,
    routerBasePath: import.meta.env.VITE_ROUTER_BASE_PATH,
    applicationName: import.meta.env.VITE_APPLICATION_NAME,
    versionId:
      import.meta.env.MODE === 'development' ? 'LOCAL_BUILD' : import.meta.env.VITE_VERSION_ID,
    theme: 'glacier'
  } as Env);

  useEffect(() => {
    axios
      .get(getEnvUrl(import.meta.env.BASE_URL))
      .then((response) => {
        const validatedConfig = validateEnv(response.data, config);
        setConfig(validatedConfig);
        stateService.setStore(validatedConfig);
        environmentService.setStore(validatedConfig);
        const sanitizedApplicationName = DOMPurify.sanitize(validatedConfig.applicationName);
        setTitle(sanitizedApplicationName);
        setDoneLoadingConfig(true);
        logger.setConfig({
          logLevel: validatedConfig.logLevel,
          shouldTrace: import.meta.env.MODE === 'development'
        });
      })
      .catch((err) => {
        throw new Error('Failed to fetch environment config file.', err);
      });
  }, []);
  const sharedConfig = {
    ...config,
    setConfig
  };

  return (
    <SharedConfigProvider value={sharedConfig}>
      {doneLoadingConfig && children}
    </SharedConfigProvider>
  );
};

export const useConfig = () => {
  return useSharedConfig() as Env & {
    setConfig: Dispatch<SetStateAction<Env>>;
    saveFeatureFlagsToLocalStorage: () => void;
    resetFeatureFlagsToServer: () => void;
  };
};
